import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Prompt library — what care managers see under "Prompts" in an agent's chat.
// Backed by ai.AgentPromptLibrary + /api/AgentPromptLibrary/* (RestApi.Tenant).
// Surfaces on Agent detail → Prompt library. Separate from Test prompts, which
// stay an internal QA tool; the Import button copies from them one way.
//
// There is no delete: switching a prompt off hides it from the chat and keeps
// it, so a later import cannot bring it back.
// ─────────────────────────────────────────────────────────────────────────────

/** OO.Domain.AIInfo.PromptSendMode — serialised as a number. */
export const PromptSendMode = {
  /** Sent straight away as the care manager's own message. */
  Send: 0,
  /** Put in the input box to edit first; nothing is sent. */
  Draft: 1,
} as const;
export type PromptSendMode = (typeof PromptSendMode)[keyof typeof PromptSendMode];

export interface AgentLibraryPrompt {
  id: number;
  agentId: number;
  title: string;
  prompt: string;
  category?: string | null;
  description?: string | null;
  sendMode: PromptSendMode;
  /** ai.AiAgentTestPrompt.Id this row was imported from; null when authored here. */
  sourceTestPromptId?: number | null;
  isActive: boolean;
  displayOrder: number;
  createdDate?: string | null;
  lastUpdatedDate?: string | null;
}

export interface PromptLibraryImportResult {
  isSuccess: boolean;
  agentId: number;
  added: number;
  alreadyPresent: number;
  skippedEmpty: number;
  message?: string | null;
}

function unwrap<T>(wrapped: OperationResult<T>, fallback: string): T {
  if (!wrapped.isSuccess) throw new Error(wrapped.message || fallback);
  return wrapped.data as T;
}

/** Every prompt for one agent, including switched-off ones, in drawer order. */
export async function getPromptLibrary(agentId: number): Promise<AgentLibraryPrompt[]> {
  const wrapped = await apiGet<OperationResult<AgentLibraryPrompt[]>>(
    `/api/AgentPromptLibrary/ForAgent/${agentId}?includeInactive=true`,
  );
  return unwrap(wrapped, 'Could not load the prompt library') ?? [];
}

/** Add (id 0) or edit. The server trims, validates and keeps the agent fixed. */
export async function savePromptLibraryRow(row: Partial<AgentLibraryPrompt>): Promise<AgentLibraryPrompt> {
  const wrapped = await apiPost<OperationResult<AgentLibraryPrompt>>('/api/AgentPromptLibrary/Save', row);
  return unwrap(wrapped, 'Could not save the prompt');
}

/** Hide from the chat. The row stays. */
export async function deactivatePromptLibraryRow(id: number): Promise<AgentLibraryPrompt> {
  const wrapped = await apiPost<OperationResult<AgentLibraryPrompt>>(`/api/AgentPromptLibrary/Deactivate/${id}`, {});
  return unwrap(wrapped, 'Could not switch the prompt off');
}

/** Drag-to-reorder: ids in their new order, saved in one call. Returns rows changed. */
export async function reorderPromptLibrary(agentId: number, orderedIds: number[]): Promise<number> {
  const wrapped = await apiPost<OperationResult<number>>(`/api/AgentPromptLibrary/Reorder/${agentId}`, orderedIds);
  return unwrap(wrapped, 'Could not save the new order');
}

/** One-way copy of the agent's active test prompts that are not in the library yet. */
export async function importPromptLibraryFromTestPrompts(agentId: number): Promise<PromptLibraryImportResult> {
  const wrapped = await apiPost<OperationResult<PromptLibraryImportResult>>(
    `/api/AgentPromptLibrary/ImportFromTestPrompts/${agentId}`,
    {},
  );
  return unwrap(wrapped, 'Import failed');
}
